/**
 * ALTCHA im Browser — die Rechenarbeit, die den Spamschutz traegt.
 *
 * **Es gibt kein Script-Tag und keinen fremden Ursprung**: gerechnet wird mit
 * `crypto.subtle`, das jeder Browser mitbringt, und geholt wird die Aufgabe von
 * unserem eigenen Auth-Dienst.
 *
 * Bewusst KEINE Abhaengigkeit auf das offizielle `altcha`-Paket: dessen
 * Web-Component bringt eigenes Markup und eigene Sprache mit und waere in der
 * geteilten Anmeldehuelle ein Fremdkoerper. Das Verfahren selbst sind zwanzig
 * Zeilen, und was hier steht, laesst sich gegen den Server in derselben
 * Testdatei nachrechnen.
 *
 * Diese Datei bleibt frei von Node-APIs — sie laeuft im Browser. Die
 * Server-Haelfte steht in `altcha.ts` und darf hier nicht importiert werden.
 */

/**
 * Der Endpunkt, an dem Aufgabe und Einloesung liegen — **eine Quelle fuer beide
 * Seiten.** Er stand zuerst viermal abgeschrieben da (hier, zweimal in
 * `LoginAltcha.tsx`, einmal in `janeway-auth/proxy.ts`); eine Umbenennung haette
 * drei davon stehenlassen, und der Ausfall waere still gewesen: die
 * `publicRoutes`-Liste haette den alten Pfad weiter freigegeben, der Browser den
 * neuen gefragt und einen 307 auf /login bekommen.
 *
 * Bewusst in DIESER Datei und nicht in `altcha.ts`: jene importiert `node:crypto`
 * und ist damit fuer eine Client-Komponente unerreichbar. Diese hier ist frei von
 * Node-APIs, also koennen Browser, Route und Middleware sie gleichermassen ziehen.
 */
export const ALTCHA_PFAD = "/api/v1/altcha";

export interface AltchaAufgabe {
  algorithm: string;
  challenge: string;
  salt: string;
  signature: string;
  maxnumber: number;
}

/** Wieviele Zahlen zwischen zwei Atempausen fuer den Bildschirm probiert werden. */
const HAPPEN = 2_000;

async function hex(text: string): Promise<string> {
  const roh = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(roh), (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Sucht die Zahl, die zur Challenge passt, und verpackt das Ergebnis so, wie der
 * Server es erwartet. `null` heisst „nicht loesbar oder abgebrochen" — der
 * Aufrufer entscheidet dann, nicht diese Funktion.
 *
 * Die Schleife legt alle `HAPPEN` Versuche eine Pause ein. Ohne sie blockiert
 * sie den einzigen Faden des Browsers fuer die gesamte Rechenzeit: das Formular
 * friert ein, waehrend der Nutzer noch tippt.
 */
export async function loeseAltchaChallenge(
  aufgabe: AltchaAufgabe,
  optionen: { signal?: AbortSignal } = {},
): Promise<string | null> {
  if (aufgabe.algorithm !== "SHA-256") return null;
  for (let n = 0; n <= aufgabe.maxnumber; n++) {
    if (optionen.signal?.aborted) return null;
    if (n > 0 && n % HAPPEN === 0) await new Promise((f) => setTimeout(f, 0));
    if ((await hex(aufgabe.salt + n)) !== aufgabe.challenge) continue;
    return btoa(
      JSON.stringify({
        algorithm: aufgabe.algorithm,
        challenge: aufgabe.challenge,
        number: n,
        salt: aufgabe.salt,
        signature: aufgabe.signature,
      }),
    );
  }
  return null;
}

export type AltchaHolFehler = "netz" | "unloesbar";

/**
 * Holt eine Aufgabe vom eigenen Auth-Dienst und loest sie.
 *
 * **`fehler: "netz"` und `nachweis: null` sind zwei verschiedene Dinge**, und
 * die Unterscheidung ist die wichtigste an dieser Funktion: „ich konnte nicht
 * pruefen" darf der Aufrufer anders behandeln als „die Pruefung ging nicht
 * durch". Waere unser Auth-Dienst ausgefallen und der Aufrufer bliebe hart, kaeme
 * niemand mehr in irgendeine App der Suite — die Anmeldung selbst laeuft ueber
 * GoTrue und haengt sonst nicht an diesem Dienst.
 */
export async function holeAltchaNachweis(
  authBasis: string,
  zweck: "anmeldung" | "registrierung",
  optionen: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<{ nachweis: string | null; fehler?: AltchaHolFehler }> {
  const basis = authBasis.replace(/\/+$/, "");
  if (!basis) return { nachweis: null, fehler: "netz" };
  const hole = optionen.fetchImpl ?? fetch;

  let aufgabe: AltchaAufgabe;
  try {
    const res = await hole(`${basis}${ALTCHA_PFAD}?zweck=${zweck}`, { signal: optionen.signal });
    if (!res.ok) return { nachweis: null, fehler: "netz" };
    aufgabe = (await res.json()) as AltchaAufgabe;
    if (!aufgabe?.challenge || !aufgabe?.salt || !aufgabe?.signature) {
      return { nachweis: null, fehler: "netz" };
    }
  } catch {
    return { nachweis: null, fehler: "netz" };
  }

  const nachweis = await loeseAltchaChallenge(aufgabe, { signal: optionen.signal });
  return nachweis ? { nachweis } : { nachweis: null, fehler: "unloesbar" };
}
